import React, { useState, useEffect } from 'react';
import {
  View,
  StyleSheet,
  ScrollView,
  TouchableOpacity,
  Alert,
} from 'react-native';
import {
  Modal,
  Portal,
  Text,
  Button,
  Card,
  IconButton,
  FAB,
  Chip,
} from 'react-native-paper';
import { colors, spacing, typography } from '../constants/theme';
import { QuestionEditorModal } from './QuestionEditorModal';

interface QuestionField {
  id?: string;
  name: string;
  label: string;
  type: 'text' | 'textarea' | 'number' | 'decimal' | 'plusminus' | 'email' | 'tel' | 'date' | 'select' | 'radio' | 'file' | 'helpful_links' | 'dimensions_2d' | 'dimensions_3d';
  required?: boolean;
  placeholder?: string;
  options?: string[];
  links?: Array<{ url: string; label: string }>;
  unit?: 'in' | 'ft' | 'cm' | 'm';
}

interface QuestionListModalProps {
  visible: boolean;
  onDismiss: () => void;
  onSave: (questions: QuestionField[]) => void;
  initialQuestions: QuestionField[];
  title?: string;
}

export const QuestionListModal: React.FC<QuestionListModalProps> = ({
  visible,
  onDismiss,
  onSave,
  initialQuestions,
  title = 'Manage Questions',
}) => {
  const [questions, setQuestions] = useState<QuestionField[]>([]);
  const [showEditor, setShowEditor] = useState(false);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [editingField, setEditingField] = useState<QuestionField | undefined>();

  useEffect(() => {
    // Sync questions whenever initialQuestions changes (including updates while modal is visible)
    const questionsWithIds = initialQuestions.map((q, index) => ({
      ...q,
      id: q.id || q.name || `field_${index}`,
    }));
    setQuestions(questionsWithIds);
  }, [initialQuestions]);

  const handleAddQuestion = () => {
    setEditingField(undefined);
    setEditingIndex(null);
    setShowEditor(true);
  };

  const handleEditQuestion = (index: number) => {
    setEditingField(questions[index]);
    setEditingIndex(index);
    setShowEditor(true);
  };

  const handleDeleteQuestion = (index: number) => {
    Alert.alert(
      'Delete Question',
      `Are you sure you want to delete "${questions[index].label}"?`,
      [
        { text: 'Cancel', style: 'cancel' },
        {
          text: 'Delete',
          style: 'destructive',
          onPress: () => {
            const newQuestions = questions.filter((_, i) => i !== index);
            setQuestions(newQuestions);
          },
        },
      ]
    );
  };

  const handleSaveField = (field: QuestionField) => {
    if (editingIndex !== null) {
      // Editing existing question
      setQuestions(prev => {
        const updated = [...prev];
        updated[editingIndex] = {
          ...field,
          id: prev[editingIndex].id || field.name,
        };
        return updated;
      });
    } else {
      // Adding new question
      setQuestions(prev => {
        const newQuestion = {
          ...field,
          id: field.id || field.name || `field_${Date.now()}`,
        };
        const updated = [...prev, newQuestion];
        return updated;
      });
    }
    
    setShowEditor(false);
    setEditingField(undefined);
    setEditingIndex(null);
  };

  const handleSaveAll = () => {
    onSave(questions);
    onDismiss();
  };

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'text': return 'format-text';
      case 'textarea': return 'text-box';
      case 'number': return 'numeric';
      case 'decimal': return 'calculator';
      case 'email': return 'email';
      case 'tel': return 'phone';
      case 'date': return 'calendar';
      case 'select': return 'menu-down';
      case 'radio': return 'radiobox-marked';
      case 'file': return 'camera';
      case 'helpful_links': return 'link-variant';
      case 'dimensions_2d': return 'ruler-square';
      case 'dimensions_3d': return 'cube-outline';
      default: return 'help-circle';
    }
  };

  return (
    <>
      <Portal>
        <Modal
          visible={visible}
          onDismiss={onDismiss}
          contentContainerStyle={styles.modal}
        >
          <View style={styles.header}>
            <Text style={styles.title}>{title}</Text>
            <IconButton
              icon="close"
              size={24}
              onPress={onDismiss}
              style={styles.closeButton}
            />
          </View>

          <ScrollView
            style={styles.scrollView}
            contentContainerStyle={styles.scrollContent}
            showsVerticalScrollIndicator={true}
          >
            {questions.length === 0 ? (
              <View style={styles.emptyState}>
                <Text style={styles.emptyText}>No custom questions yet</Text>
                <Text style={styles.emptySubtext}>
                  Add questions to collect additional information
                </Text>
              </View>
            ) : (
              questions.map((question, index) => (
                <Card key={question.id || `question-${index}`} style={styles.questionCard}>
                  <Card.Content style={styles.questionContent}>
                    <View style={styles.questionHeader}>
                      <IconButton
                        icon={getTypeIcon(question.type)}
                        size={20}
                        iconColor={colors.primary}
                      />
                      <View style={styles.questionInfo}>
                        <Text style={styles.questionLabel}>{question.label}</Text>
                        <Text style={styles.questionName}>Field: {question.name}</Text>
                      </View>
                      {question.required && (
                        <Chip
                          mode="flat"
                          compact
                          style={styles.requiredChip}
                          textStyle={styles.requiredChipText}
                        >
                          Required
                        </Chip>
                      )}
                    </View>
                    <View style={styles.questionActions}>
                      <IconButton
                        icon="pencil"
                        size={20}
                        onPress={() => handleEditQuestion(index)}
                      />
                      <IconButton
                        icon="delete"
                        size={20}
                        iconColor={colors.error}
                        onPress={() => handleDeleteQuestion(index)}
                      />
                    </View>
                  </Card.Content>
                </Card>
              ))
            )}
          </ScrollView>

          <View style={styles.footer}>
            <Button
              mode="outlined"
              onPress={onDismiss}
              style={styles.footerButton}
            >
              Cancel
            </Button>
            <Button
              mode="contained"
              onPress={handleSaveAll}
              style={styles.footerButton}
            >
              Save Questions
            </Button>
          </View>

          <FAB
            icon="plus"
            style={styles.fab}
            onPress={handleAddQuestion}
            label="Add Question"
          />
        </Modal>
      </Portal>

      <QuestionEditorModal
        visible={showEditor}
        onDismiss={() => {
          setShowEditor(false);
          setEditingField(undefined);
          setEditingIndex(null);
        }}
        onSave={handleSaveField}
        existingField={editingField}
      />
    </>
  );
};

const styles = StyleSheet.create({
  modal: {
    backgroundColor: colors.background,
    margin: 20,
    marginTop: 80,
    marginBottom: 80,
    borderRadius: 12,
    height: '75%',
    display: 'flex',
    flexDirection: 'column',
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: spacing.lg,
    paddingTop: spacing.lg,
    paddingBottom: spacing.md,
    borderBottomWidth: 1,
    borderBottomColor: colors.gray100,
    backgroundColor: colors.background,
    borderTopLeftRadius: 12,
    borderTopRightRadius: 12,
  },
  title: {
    ...typography.h2,
    color: colors.text,
    flex: 1,
    marginRight: spacing.sm,
  },
  closeButton: {
    margin: 0,
  },
  scrollView: {
    flex: 1,
    backgroundColor: colors.background,
    minHeight: 100,
  },
  scrollContent: {
    padding: spacing.lg,
    paddingBottom: 120, // Extra space for FAB
    flexGrow: 1,
  },
  emptyState: {
    alignItems: 'center',
    paddingVertical: spacing.xl * 2,
  },
  emptyText: {
    ...typography.body1,
    color: colors.textSecondary,
    marginBottom: spacing.sm,
  },
  emptySubtext: {
    ...typography.caption,
    color: colors.textSecondary,
    textAlign: 'center',
  },
  questionCard: {
    marginBottom: spacing.md,
    backgroundColor: colors.surface || colors.background,
    elevation: 2,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.1,
    shadowRadius: 2,
  },
  questionContent: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    minHeight: 60,
    paddingVertical: spacing.sm,
  },
  questionHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    flex: 1,
  },
  questionInfo: {
    flex: 1,
    marginLeft: spacing.sm,
  },
  questionLabel: {
    ...typography.body1,
    fontWeight: '600',
    color: colors.text,
  },
  questionName: {
    ...typography.caption,
    color: colors.textSecondary,
    marginTop: 2,
  },
  requiredChip: {
    backgroundColor: colors.primaryLight,
    marginLeft: spacing.sm,
  },
  requiredChipText: {
    fontSize: 10,
    color: colors.primary,
  },
  questionActions: {
    flexDirection: 'row',
  },
  footer: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    alignItems: 'center',
    padding: spacing.lg,
    paddingBottom: spacing.lg + 10,
    borderTopWidth: 1,
    borderTopColor: colors.gray100,
    backgroundColor: colors.background,
    borderBottomLeftRadius: 12,
    borderBottomRightRadius: 12,
  },
  footerButton: {
    minWidth: 100,
    marginLeft: spacing.md,
  },
  fab: {
    position: 'absolute',
    right: spacing.lg,
    bottom: 90,
    backgroundColor: colors.primary,
    elevation: 6,
  },
});
